import * as React from 'react';
import { useDraftNavigationGuard } from '@/hooks/use-draft-navigation-guard';

export interface FormIssue { fieldId?: string; message: string }
export function FormFeedback({ issues, pending = false }: { issues: FormIssue[]; pending?: boolean }) {
  const summary = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (issues.length) summary.current?.focus();
  }, [issues]);
  if (!issues.length) return pending ? <p role="status" className="text-sm text-muted-foreground">Saving changes…</p> : null;
  return <div ref={summary} role="alert" tabIndex={-1}
    className="rounded-md border border-destructive p-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
    <p className="font-medium">Your changes have not been saved</p>
    <ul className="mt-2 list-disc space-y-1 pl-5">
      {issues.map(issue => <li key={issue.fieldId ?? issue.message}>{issue.fieldId
        ? <a href={'#' + issue.fieldId} className="underline" onClick={event => {
          event.preventDefault(); document.getElementById(issue.fieldId ?? '')?.focus();
        }}>{issue.message}</a> : issue.message}</li>)}
    </ul>
  </div>;
}

/** Protect dismissal and ignore late completion after a scope/remount boundary. */
export function useRetainedForm(pending: boolean, onOpenChange: (open: boolean) => void) {
  const [dirty, setDirty] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const busy = React.useRef(false);
  const mounted = React.useRef(true);
  React.useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useDraftNavigationGuard(dirty, pending || saving);
  return {
    dirty, pending: pending || saving, markDirty: () => setDirty(true),
    markSaved: () => { if (mounted.current) setDirty(false); },
    requestClose: () => {
      if (!pending && !busy.current) onOpenChange(false);
    },
    discard: (reset: () => void) => {
      if (pending || busy.current || (dirty && !window.confirm('Discard your unsaved changes?'))) return;
      setDirty(false); reset(); onOpenChange(false);
    },
    async submit(run: () => Promise<void>, failed: (error: unknown) => void) {
      if (pending || busy.current) return;
      busy.current = true; setSaving(true);
      try { await run(); if (mounted.current) { setDirty(false); onOpenChange(false); } }
      catch (error) { if (mounted.current) failed(error); }
      finally { busy.current = false; if (mounted.current) setSaving(false); }
    }
  };
}
